import { useState, useEffect } from "react";
import { RichEditor } from "./RichEditor";

type Post = {
  id: number;
  title: string;
  slug: string;
  content: string;
  published: 0 | 1;
};

type Props = {
  token: string;
  postId: number | null;
  onSaved: () => void;
  onCancel: () => void;
};

function toSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[äöüß]/g, (c) =>
      ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" })[c] ?? c,
    )
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function AdminPostEditor({ token, postId, onSaved, onCancel }: Props) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [published, setPublished] = useState<0 | 1>(0);
  const [editorReady, setEditorReady] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  useEffect(() => {
    if (postId === null) {
      setTitle("");
      setSlug("");
      setContent("");
      setPublished(0);
      setSlugTouched(false);
      setEditorReady(true);
      return;
    }
    setEditorReady(false);
    fetch(`/api/admin/posts/${postId}`, { headers })
      .then((r) => r.json())
      .then((data) => {
        const p = data as Post;
        setTitle(p.title);
        setSlug(p.slug);
        setContent(p.content);
        setPublished(p.published);
        setSlugTouched(true);
        setEditorReady(true);
      });
  }, [postId]);

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!slugTouched) setSlug(toSlug(val));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title || !slug) {
      setError("Titel und Slug sind Pflichtfelder.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const body = JSON.stringify({ title, slug, content, published });
      const res =
        postId === null
          ? await fetch("/api/admin/posts", { method: "POST", headers, body })
          : await fetch(`/api/admin/posts/${postId}`, {
              method: "PUT",
              headers,
              body,
            });
      if (!res.ok) {
        const err = (await res.json()) as { error: string };
        setError(err.error ?? "Fehler beim Speichern.");
        return;
      }
      onSaved();
    } catch {
      setError("Verbindungsfehler.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-page">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="admin-wrap">
        <header className="admin-header">
          <h1 className="admin-title">
            {postId === null ? "Neuer Beitrag" : "Beitrag bearbeiten"}
          </h1>
          <div className="admin-header-actions">
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              onClick={onCancel}
            >
              Abbrechen
            </button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="admin-editor-form">
          {error && <p className="admin-error">{error}</p>}

          <div className="admin-fields">
            <div className="admin-field">
              <label className="admin-label">Titel</label>
              <input
                className="admin-input"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                required
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Slug</label>
              <input
                className="admin-input admin-input--mono"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugTouched(true);
                }}
                required
              />
            </div>
            <div className="admin-field admin-field--inline">
              <label className="admin-label">
                <input
                  type="checkbox"
                  checked={published === 1}
                  onChange={(e) => setPublished(e.target.checked ? 1 : 0)}
                  className="admin-checkbox"
                />
                Veröffentlicht
              </label>
            </div>
          </div>

          <div className="admin-rich-wrap">
            <label className="admin-label">Inhalt</label>
            {editorReady ? (
              <RichEditor
                key={postId ?? "new"}
                initialContent={content}
                onChange={setContent}
                token={token}
              />
            ) : (
              <div className="admin-editor-loading">Lade…</div>
            )}
          </div>

          <div className="admin-editor-footer">
            <button className="admin-btn" type="submit" disabled={loading}>
              {loading ? "Speichert…" : "Speichern"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
